import React, { useState, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { sessionService } from '../../services/api';
import DenominationCounter from './DenominationCounter';

const FONT = "'Plus Jakarta Sans', sans-serif";

const COLORS = {
    bg: '#0f172a',
    card: '#1e293b',
    cardHover: '#334155',
    border: '#334155',
    text: '#f8fafc',
    muted: '#94a3b8',
    green: '#22c55e',
    greenDark: '#16a34a',
    greenBg: 'rgba(34, 197, 94, 0.1)',
    greenBorder: 'rgba(34, 197, 94, 0.3)',
    red: '#ef4444',
    redBg: 'rgba(239, 68, 68, 0.1)',
    redBorder: 'rgba(239, 68, 68, 0.3)',
    amber: '#f59e0b',
    amberBg: 'rgba(245, 158, 11, 0.1)',
};

const DEFAULT_DENOMINATIONS = [
    { value: 50, type: 'note' },
    { value: 20, type: 'note' },
    { value: 10, type: 'note' },
    { value: 5, type: 'note' },
    { value: 2, type: 'coin' },
    { value: 1, type: 'coin' },
    { value: 0.5, type: 'coin' },
    { value: 0.2, type: 'coin' },
    { value: 0.1, type: 'coin' },
    { value: 0.05, type: 'coin' },
    { value: 0.02, type: 'coin' },
    { value: 0.01, type: 'coin' },
];

const formatDuration = (startTime) => {
    if (!startTime) return '--';
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};

/**
 * EndOfDayFlow - Multi-step end-of-day flow for cashiers.
 *
 * Props:
 *   session        - The active CashierSession object
 *   pendingOrders  - Array of orders not yet completed/paid
 *   onSessionClosed - Callback invoked with the closed session object
 *   onCancel       - Callback to return to POS
 *   treatAlreadyClosedAsClosed - (User-QC finding C, additive/default-off)
 *       When the close request 400s with detail 'Session is not open', the
 *       session is already closed server-side — success-equivalent, nothing
 *       left to retry. Off by default so every existing caller (legacy web/
 *       Electron CashierSalesPage) keeps its byte-identical hard-error
 *       behavior; the native surface opts in.
 */
const EndOfDayFlow = ({
    session, pendingOrders = [], onSessionClosed, onCancel,
    treatAlreadyClosedAsClosed = false,
}) => {
    const { t } = useLanguage();
    const { getCurrency, getTenantSetting } = useAuth();
    const currency = getCurrency();

    const denominations = getTenantSetting('eod_denominations', DEFAULT_DENOMINATIONS);

    // Determine initial step: if pending orders exist, start blocked
    const hasPending = pendingOrders.length > 0;
    const [step, setStep] = useState(hasPending ? 'blocked' : 'count');
    const [cashCounted, setCashCounted] = useState(0);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [closedSession, setClosedSession] = useState(null);

    const [denomQuantities, setDenomQuantities] = useState({});

    const handleTotalChange = useCallback((total, quantities) => {
        setCashCounted(total);
        if (quantities) setDenomQuantities(quantities);
    }, []);

    const handleCloseSession = async () => {
        setLoading(true);
        setError(null);
        try {
            // Build denomination_count array from quantities
            const denomination_count = denominations
                .filter(d => (denomQuantities[d.value] || 0) > 0)
                .map(d => ({ value: d.value, type: d.type, qty: denomQuantities[d.value] || 0 }));

            const result = await sessionService.closeSession(session.session_id, {
                end_cash: cashCounted,
                denomination_count,
                notes: notes || undefined,
            });
            setClosedSession(result);
            setStep('confirm');
            if (onSessionClosed) onSessionClosed(result);
        } catch (err) {
            console.error('Failed to close session:', err);
            const detail = err?.response?.data?.detail;
            // User-QC finding C: a 400 'Session is not open' means the
            // session is ALREADY closed (e.g. a queued mutation replayed it,
            // or a prior attempt actually succeeded but the response was
            // lost) — there's nothing left to retry, so treat it as
            // success-equivalent instead of a hard error. Additive/
            // default-off: legacy behavior (hard error, stay on this step)
            // is unchanged unless a caller explicitly opts in.
            if (treatAlreadyClosedAsClosed && detail === 'Session is not open') {
                setStep('confirm');
                if (onSessionClosed) onSessionClosed(session);
                return;
            }
            const msg = typeof detail === 'string' ? detail
                : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
                : t('failed_to_close_session') || 'Failed to close session';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // ── BLOCKED STEP ──
    if (step === 'blocked') {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    background: COLORS.bg,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: FONT,
                    padding: 24,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: 480,
                        borderRadius: 16,
                        overflow: 'hidden',
                        background: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
                    }}
                >
                    {/* Red header */}
                    <div
                        style={{
                            background: `linear-gradient(135deg, ${COLORS.red}, #dc2626)`,
                            padding: '24px',
                            textAlign: 'center',
                        }}
                    >
                        <div style={{ fontSize: 32, marginBottom: 6 }}>&#9888;&#65039;</div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#fff' }}>
                            {t('pending_orders_found') || 'Pending Orders Found'}
                        </h2>
                        <p style={{ margin: '6px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
                            {t('close_pending_orders_first') || 'Close or void all pending orders before ending your session'}
                        </p>
                    </div>

                    {/* Pending orders list */}
                    <div style={{ padding: 20, maxHeight: 300, overflowY: 'auto' }}>
                        {pendingOrders.map((order, idx) => (
                            <div
                                key={order.id || idx}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px 14px',
                                    borderRadius: 8,
                                    marginBottom: 6,
                                    background: COLORS.redBg,
                                    border: `1px solid ${COLORS.redBorder}`,
                                }}
                            >
                                <div>
                                    <span style={{ color: COLORS.text, fontWeight: 600, fontSize: 13 }}>
                                        #{order.order_number || order.id}
                                    </span>
                                    {order.table_name && (
                                        <span style={{ color: COLORS.muted, fontSize: 12, marginLeft: 8 }}>
                                            {order.table_name}
                                        </span>
                                    )}
                                </div>
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        color: COLORS.amber,
                                        background: COLORS.amberBg,
                                        padding: '3px 8px',
                                        borderRadius: 4,
                                    }}
                                >
                                    {order.status || 'pending'}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Buttons */}
                    <div style={{ padding: '0 20px 20px' }}>
                        <button
                            onClick={onCancel}
                            style={{
                                width: '100%',
                                padding: '14px 0',
                                borderRadius: 10,
                                border: `1px solid ${COLORS.border}`,
                                background: COLORS.bg,
                                color: COLORS.text,
                                fontSize: 14,
                                fontWeight: 700,
                                cursor: 'pointer',
                                fontFamily: FONT,
                            }}
                        >
                            {t('back_to_pos') || 'Back to POS'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── CONFIRM STEP ──
    if (step === 'confirm') {
        const displaySession = closedSession || session;
        return (
            <div
                style={{
                    minHeight: '100vh',
                    background: COLORS.bg,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: FONT,
                    padding: 24,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: 440,
                        borderRadius: 16,
                        overflow: 'hidden',
                        background: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
                    }}
                >
                    {/* Green header */}
                    <div
                        style={{
                            background: `linear-gradient(135deg, ${COLORS.green}, ${COLORS.greenDark})`,
                            padding: '28px 24px',
                            textAlign: 'center',
                        }}
                    >
                        <div style={{ fontSize: 36, marginBottom: 8 }}>&#9989;</div>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#fff' }}>
                            {t('session_closed') || 'Session Closed'}
                        </h2>
                    </div>

                    {/* Summary */}
                    <div style={{ padding: 24 }}>
                        {/* Cash counted */}
                        <div style={{ textAlign: 'center', marginBottom: 20 }}>
                            <span style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('cash_counted') || 'Cash Counted'}
                            </span>
                            <div style={{ fontSize: 32, fontWeight: 800, color: COLORS.green, marginTop: 4 }}>
                                {currency}{cashCounted.toFixed(2)}
                            </div>
                        </div>

                        {/* Info rows */}
                        <div
                            style={{
                                borderRadius: 10,
                                background: COLORS.bg,
                                padding: 16,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 13, color: COLORS.muted }}>
                                    {t('session_duration') || 'Session Duration'}
                                </span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
                                    {formatDuration(displaySession.start_time)}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 13, color: COLORS.muted }}>
                                    {t('total_transactions') || 'Total Transactions'}
                                </span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
                                    {displaySession.transaction_count ?? '--'}
                                </span>
                            </div>
                        </div>

                        {/* Done button */}
                        <button
                            onClick={onCancel}
                            style={{
                                width: '100%',
                                marginTop: 20,
                                padding: '14px 0',
                                borderRadius: 10,
                                border: 'none',
                                background: `linear-gradient(135deg, ${COLORS.green}, ${COLORS.greenDark})`,
                                color: '#fff',
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: 'pointer',
                                fontFamily: FONT,
                            }}
                        >
                            {t('done') || 'Done'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── COUNT STEP (default) ──
    return (
        <div
            style={{
                minHeight: '100vh',
                background: COLORS.bg,
                display: 'flex',
                flexDirection: 'column',
                fontFamily: FONT,
            }}
        >
            {/* Header */}
            <div
                style={{
                    background: COLORS.card,
                    borderBottom: `1px solid ${COLORS.border}`,
                    padding: '16px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: COLORS.text }}>
                        {t('end_of_day') || 'End of Day'}
                    </h2>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: COLORS.muted }}>
                        {t('count_your_cash') || 'Count your cash drawer'}
                    </p>
                </div>
                <button
                    onClick={onCancel}
                    style={{
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: `1px solid ${COLORS.border}`,
                        background: 'transparent',
                        color: COLORS.muted,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: FONT,
                    }}
                >
                    {t('cancel') || 'Cancel'}
                </button>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 24, maxWidth: 640, margin: '0 auto', width: '100%' }}>
                <DenominationCounter
                    denominations={denominations}
                    currency={currency}
                    onTotalChange={handleTotalChange}
                />

                {/* Notes */}
                <div style={{ marginTop: 20 }}>
                    <label
                        style={{
                            display: 'block',
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 0.8,
                            color: COLORS.muted,
                            marginBottom: 8,
                        }}
                    >
                        {t('notes') || 'Notes'} ({t('optional') || 'optional'})
                    </label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t('eod_notes_placeholder') || 'Any notes about this session...'}
                        rows={3}
                        style={{
                            width: '100%',
                            padding: '12px 14px',
                            borderRadius: 10,
                            border: `1px solid ${COLORS.border}`,
                            background: COLORS.card,
                            color: COLORS.text,
                            fontSize: 13,
                            outline: 'none',
                            resize: 'vertical',
                            fontFamily: FONT,
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* Error */}
                {error && (
                    <div
                        style={{
                            marginTop: 12,
                            padding: '10px 14px',
                            borderRadius: 8,
                            background: COLORS.redBg,
                            border: `1px solid ${COLORS.redBorder}`,
                            color: '#f87171',
                            fontSize: 13,
                            textAlign: 'center',
                        }}
                    >
                        {error}
                    </div>
                )}

                {/* Submit */}
                <button
                    onClick={handleCloseSession}
                    disabled={loading}
                    style={{
                        width: '100%',
                        marginTop: 20,
                        padding: '14px 0',
                        borderRadius: 10,
                        border: 'none',
                        background: loading
                            ? COLORS.border
                            : `linear-gradient(135deg, ${COLORS.green}, ${COLORS.greenDark})`,
                        color: '#fff',
                        fontSize: 15,
                        fontWeight: 700,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontFamily: FONT,
                        transition: 'all 0.15s ease',
                        marginBottom: 24,
                    }}
                >
                    {loading
                        ? (t('closing_session') || 'Closing...')
                        : (t('close_session') || 'Close Session')}
                </button>
            </div>
        </div>
    );
};

export default EndOfDayFlow;
